import axios from './axiosConfig'

export interface DailySpread {
    id: string
    title: string
    positions: string[]
    scheduledDate: string | null
    createdAt: string
}

export interface CreateDailySpreadInput {
    title: string
    positions: string[]
    scheduledDate?: string | null
}

export interface UpdateDailySpreadInput {
    title?: string
    positions?: string[]
    scheduledDate?: string | null
}

// Helper to get local date in YYYY-MM-DD format
const getLocalDateString = (): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

// Get all spreads (admin only)
export const getAllDailySpreads = async (): Promise<DailySpread[]> => {
    const response = await axios.get('/daily-spread')
    return response.data
}

// Get today's spread (based on user's local date)
export const getTodaysSpread = async (): Promise<DailySpread> => {
    const localDate = getLocalDateString()
    const response = await axios.get(`/daily-spread/today?localDate=${localDate}`)
    return response.data
}

// Create a new spread (admin only)
export const createDailySpread = async (input: CreateDailySpreadInput): Promise<DailySpread> => {
    const response = await axios.post('/daily-spread', input)
    return response.data
}

// Update a spread (admin only)
export const updateDailySpread = async (id: string, input: UpdateDailySpreadInput): Promise<DailySpread> => {
    const response = await axios.put(`/daily-spread/${id}`, input)
    return response.data
}

// Delete a spread (admin only)
export const deleteDailySpread = async (id: string): Promise<void> => {
    await axios.delete(`/daily-spread/${id}`)
}

// Card result for a spread position
export interface CardResult {
    name: string
    artwork: string
    reversed: boolean
}

// Daily spread result
export interface DailySpreadResult {
    id: string
    userId: string
    spreadId: string
    date: string
    cards: CardResult[]
    interpretation?: string | null
    savedToJournal?: boolean
    createdAt: string
    spread: DailySpread
}

// Get today's result for the current user (based on user's local date)
export const getTodaysResult = async (): Promise<DailySpreadResult | null> => {
    try {
        const localDate = getLocalDateString()
        const response = await axios.get(`/daily-spread/result/today?localDate=${localDate}`)
        return response.data
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null
        }
        throw error
    }
}

// Save the result for today's spread (based on user's local date)
export const saveDailySpreadResult = async (spreadId: string, cards: CardResult[]): Promise<DailySpreadResult> => {
    const localDate = getLocalDateString()
    const response = await axios.post('/daily-spread/result', { spreadId, cards, localDate })
    return response.data
}

// Interpretation input for a card
export interface InterpretationCardInput {
    position: string
    cardName: string
    meaning?: string
    reversed: boolean
}

// Generate interpretation for a spread
// If resultId is provided, the interpretation will be saved to the result (once only)
export const generateInterpretation = async (
    spreadTitle: string,
    cards: InterpretationCardInput[],
    resultId?: string
): Promise<string> => {
    const response = await axios.post('/daily-spread/result/interpretation', {
        spreadTitle,
        cards,
        resultId
    })
    return response.data.interpretation
}
